/**
 * Shared Timezone Display Formatters
 * Enforces explicit warehouse timezone for all operational timestamps.
 */

interface PermanentZoneRule {
    canonicalIanaZone: string;
    startUtcMs: number;
    startLocalDate: string;
    offsetMinutes: number;
    zoneLabel: string;
}

const IANA_2026E_PERMANENT_ZONES: Record<string, PermanentZoneRule> = {
    'America/Vancouver': {
        canonicalIanaZone: 'America/Dawson_Creek',
        startUtcMs: Date.UTC(2026, 2, 8, 10, 0, 0), // 2026-03-08T10:00:00.000Z
        startLocalDate: '2026-03-09',
        offsetMinutes: -420,
        zoneLabel: 'PT',
    },
    'Canada/Pacific': {
        canonicalIanaZone: 'America/Dawson_Creek',
        startUtcMs: Date.UTC(2026, 2, 8, 10, 0, 0),
        startLocalDate: '2026-03-09',
        offsetMinutes: -420,
        zoneLabel: 'PT',
    },
    'America/Edmonton': {
        canonicalIanaZone: 'America/Regina',
        startUtcMs: Date.UTC(2026, 2, 8, 9, 0, 0), // 2026-03-08T09:00:00.000Z
        startLocalDate: '2026-03-09',
        offsetMinutes: -360,
        zoneLabel: 'MT',
    },
    'Canada/Mountain': {
        canonicalIanaZone: 'America/Regina',
        startUtcMs: Date.UTC(2026, 2, 8, 9, 0, 0),
        startLocalDate: '2026-03-09',
        offsetMinutes: -360,
        zoneLabel: 'MT',
    },
    'America/Winnipeg': {
        canonicalIanaZone: 'America/Atikokan',
        startUtcMs: Date.UTC(2026, 2, 8, 8, 0, 0), // 2026-03-08T08:00:00.000Z
        startLocalDate: '2026-03-09',
        offsetMinutes: -300,
        zoneLabel: 'CT',
    },
    'Canada/Central': {
        canonicalIanaZone: 'America/Atikokan',
        startUtcMs: Date.UTC(2026, 2, 8, 8, 0, 0),
        startLocalDate: '2026-03-09',
        offsetMinutes: -300,
        zoneLabel: 'CT',
    },
};

/**
 * Resolves the effective IANA timezone accounting for IANA 2026e Canadian permanent-time
 * transitions (BC UTC-7, Alberta UTC-6, Manitoba UTC-5 after March 8, 2026),
 * ensuring consistent UTC offset calculation even on runtimes with pre-2026e tzdata.
 */
export function resolveEffectiveTimeZone(
    timeZone: string | null | undefined,
    instantOrLocalDate?: string | Date | number | null
): string | null | undefined {
    if (!timeZone) {
        return timeZone;
    }
    const rule = IANA_2026E_PERMANENT_ZONES[timeZone];
    if (!rule) {
        return timeZone;
    }

    if (typeof instantOrLocalDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(instantOrLocalDate)) {
        return instantOrLocalDate >= rule.startLocalDate
            ? rule.canonicalIanaZone
            : timeZone;
    }

    if (instantOrLocalDate !== undefined && instantOrLocalDate !== null) {
        const ms =
            instantOrLocalDate instanceof Date
                ? instantOrLocalDate.getTime()
                : typeof instantOrLocalDate === 'number'
                  ? instantOrLocalDate
                  : Date.parse(instantOrLocalDate);
        if (!Number.isNaN(ms)) {
            return ms >= rule.startUtcMs
                ? rule.canonicalIanaZone
                : timeZone;
        }
    }

    return Date.now() >= rule.startUtcMs
        ? rule.canonicalIanaZone
        : timeZone;
}

export function resolveEffectiveZoneLabel(
    originalTz: string,
    utcMillis: number,
    offsetMinutes: number,
    fallbackLabel: string
): string {
    const rule = IANA_2026E_PERMANENT_ZONES[originalTz];
    if (rule && utcMillis >= rule.startUtcMs && offsetMinutes === rule.offsetMinutes) {
        return rule.zoneLabel;
    }
    return fallbackLabel;
}

/**
 * Safely format an absolute instant into a wall-clock string using the provided timezone.
 * Never defaults to browser timezone for operational events.
 */
export function formatInWarehouseTimeZone(
    instant: string | Date | number | null | undefined, 
    timeZone: string | null | undefined, 
    options: Intl.DateTimeFormatOptions = {}
): string {
    if (!instant) return '';
    if (!timeZone) {
        console.warn('formatInWarehouseTimeZone: missing timezone, returning "--" to avoid unsafe fallback');
        return '--';
    }

    try {
        const date = new Date(instant);
        if (isNaN(date.getTime())) return '';
        const effectiveTimeZone = resolveEffectiveTimeZone(timeZone, date) || timeZone;
        
        return new Intl.DateTimeFormat('en-US', {
            timeZone: effectiveTimeZone,
            ...options
        }).format(date);
    } catch (err) {
        console.error('formatInWarehouseTimeZone error:', err);
        return '';
    }
}

/**
 * Convenience method for common 'HH:mm' time output
 */
export function formatTimeInTimeZone(instant: string | Date | number | null | undefined, timeZone: string | null | undefined): string {
    return formatInWarehouseTimeZone(instant, timeZone, {
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false
    });
}

/**
 * Convenience method for common 'MMM d' or 'MMM d, h:mm a' output
 */
export function formatDateInTimeZone(instant: string | Date | number | null | undefined, timeZone: string | null | undefined, includeTime = false): string {
    return formatInWarehouseTimeZone(instant, timeZone, {
        month: 'short',
        day: 'numeric',
        ...(includeTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {})
    });
}

/**
 * Given an absolute instant, derive the exact YYYY-MM-DD and HH:mm strings
 * in the specified timezone. Use this ONLY to populate datetime-local 
 * or date/time edit controls safely without browser shift.
 */
export function getWarehouseLocalComponents(instant: string | Date | number | null | undefined, timeZone: string | null | undefined): { date: string, time: string } {
    if (!instant || !timeZone) return { date: '', time: '' };
    
    try {
        const date = new Date(instant);
        if (isNaN(date.getTime())) return { date: '', time: '' };
        const effectiveTimeZone = resolveEffectiveTimeZone(timeZone, date) || timeZone;

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: effectiveTimeZone,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
            hour12: false
        });

        const parts = formatter.formatToParts(date);
        const p: Record<string, string> = {};
        for (const part of parts) {
            p[part.type] = part.value;
        }

        let hour = p.hour || '00';
        if (hour === '24') hour = '00';

        return {
            date: `${p.year}-${p.month}-${p.day}`,
            time: `${hour}:${p.minute || '00'}`
        };
    } catch {
        return { date: '', time: '' };
    }
}

import { DateTime } from 'luxon';

/**
 * Formats a naive warehouse-local wall-clock string (YYYY-MM-DDTHH:mm) for display.
 * Extracts the exact structural components and formats them in a neutral UTC zone
 * to guarantee no browser or DST normalization shifts the supplied digits.
 */
export function formatLocalWallClock(naiveString: string | null | undefined, formatString: string): string {
    if (!naiveString) return '';
    
    // Strictly extract components
    const match = naiveString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?(?:\.\d{3}Z?)?$/);
    if (!match) return naiveString; // Malformed, fail safely by returning original
    
    const [, yearStr, monthStr, dayStr, hourStr, minuteStr] = match;
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    
    // Parse inside neutral UTC container to prevent DST shifting
    const dt = DateTime.fromObject(
        { year, month, day, hour, minute }, 
        { zone: 'UTC' }
    );
    
    if (dt.isValid) {
        return dt.toFormat(formatString);
    }
    
    return naiveString;
}
